import type { AccessDeviceManagementAttempt } from '@/api/core/access-device-management';
import { putAccessDeviceManagement } from '@/api/core/access-device-management';
import type { AccessDeviceRegistryItem } from '@/api/core/access-device-registry';
import { canWriteProperties } from '@/api/core/authorization';
import type { NativeProperty } from '@/api/core/native-property-read';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import { UnsavedGuard } from '@/components/unsaved-guard';
import {
  Alert,
  AlertDescription,
  Button,
  ConfirmDialog,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useEffect,
  useInsertionEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  accessDeviceCommandContextQuery,
  nativePropertiesQuery,
  nativePropertyCatalogQuery,
  type PropertyRuntime,
} from '../property/property-queries';

interface Values {
  deviceId: string;
  gatewayId: string;
  deviceType: string;
  deviceTypeProvided: boolean;
  deviceName: string;
  deviceNameProvided: boolean;
  gatewayName: string;
  gatewayNameProvided: boolean;
  battery: string;
  isActive: boolean;
}
interface ScopedAttempt extends AccessDeviceManagementAttempt {
  readonly apiNamespace: string;
  readonly generation: number;
}

function fromDevice(device: AccessDeviceRegistryItem): Values {
  return {
    deviceId: device.device_id,
    gatewayId: device.gateway_id ?? '',
    deviceType: device.device_type ?? '',
    deviceTypeProvided: device.device_type !== null,
    deviceName: device.device_name ?? '',
    deviceNameProvided: device.device_name !== null,
    gatewayName: device.gateway_name ?? '',
    gatewayNameProvided: device.gateway_name !== null,
    battery: device.battery === null ? '' : String(device.battery),
    isActive: device.is_active,
  };
}

function messageFor(error: unknown): string {
  if (isApiError(error)) {
    if (error.status === 400) return '입력값을 확인해 주세요. 입력한 내용은 보존했습니다.';
    if (error.status === 403) return '현재 지점의 수정 권한을 확인할 수 없습니다.';
    if (error.status === 404)
      return '수정할 지점 범위를 확인하지 못했습니다. 지점과 환경을 다시 확인해 주세요.';
    if (error.status === 409)
      return '저장된 정보가 바뀌었습니다. 초안은 보존했습니다. 최신 정보를 다시 확인해 주세요.';
    if (
      error.kind === 'network' ||
      error.kind === 'timeout' ||
      error.kind === 'invalid-response' ||
      (error.status !== undefined && error.status >= 500)
    )
      return '저장 결과를 확인하지 못했습니다. 같은 요청을 자동으로 다시 보내지 않았습니다.';
  }
  return '변경 결과를 확인하지 못했습니다. 입력한 내용은 보존했습니다.';
}

function validOpaqueId(value: string): boolean {
  const points = Array.from(value, (character) => character.codePointAt(0) ?? 0);
  return (
    points.length >= 1 &&
    points.length <= 50 &&
    !points.some((point) => (point >= 0 && point <= 31) || (point >= 127 && point <= 159))
  );
}

function labelFor(properties: NativeProperty[], propertyId: string): string | undefined {
  const property = properties.find((item) => item.id === propertyId);
  return property ? `${property.name} · ${property.code}` : undefined;
}

export function AccessDeviceMetadataEditForm({
  runtime,
  generation,
  device,
  onClose,
  refreshRegistry,
}: {
  runtime: PropertyRuntime;
  generation: number;
  device: AccessDeviceRegistryItem;
  onClose: () => void;
  refreshRegistry: () => Promise<AccessDeviceRegistryItem | undefined>;
}) {
  const session = useOperatorSession(runtime.session);
  const queryClient = useQueryClient();
  const [catalogCursor, setCatalogCursor] = useState<string | null>(null);
  const [propertyId, setPropertyId] = useState('');
  const [values, setValues] = useState(() => fromDevice(device));
  const [attempt, setAttempt] = useState<ScopedAttempt | null>(null);
  const [pending, setPending] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState<string>();
  const [receipt, setReceipt] = useState<string>();
  const [savedRevision, setSavedRevision] = useState<number>();
  const [latestDevice, setLatestDevice] = useState<AccessDeviceRegistryItem>();
  const [reconcileRequired, setReconcileRequired] = useState(false);
  const [reviewedSnapshotKey, setReviewedSnapshotKey] = useState<string>();
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'close' | 'abandon'>('close');
  const flight = useRef<object | null>(null);
  const controller = useRef<AbortController | null>(null);
  const originalRevision = useRef(device.revision);
  const activeScope = useRef({
    apiNamespace: runtime.apiNamespace,
    generation,
    deviceId: device.core_device_id,
    propertyId,
  });
  useInsertionEffect(() => {
    activeScope.current = {
      apiNamespace: runtime.apiNamespace,
      generation,
      deviceId: device.core_device_id,
      propertyId,
    };
  }, [runtime.apiNamespace, generation, device.core_device_id, propertyId]);
  const canWrite =
    session.status === 'authorized' &&
    session.generation === generation &&
    !session.deniedDomains.includes('property') &&
    Boolean(session.authorization && canWriteProperties(session.authorization));
  const properties = useQuery(nativePropertiesQuery(runtime, generation, catalogCursor, canWrite));
  const candidateContext = useQuery(
    accessDeviceCommandContextQuery(runtime, generation, device.core_device_id, null, true, () => {
      const current = runtime.session.getSnapshot();
      return (
        current.status === 'authorized' &&
        current.generation === generation &&
        activeScope.current.apiNamespace === runtime.apiNamespace &&
        activeScope.current.generation === generation &&
        activeScope.current.deviceId === device.core_device_id
      );
    }),
  );
  const context = useQuery(
    accessDeviceCommandContextQuery(
      runtime,
      generation,
      device.core_device_id,
      propertyId || null,
      true,
      () => {
        const current = runtime.session.getSnapshot();
        return (
          current.status === 'authorized' &&
          current.generation === generation &&
          activeScope.current.apiNamespace === runtime.apiNamespace &&
          activeScope.current.generation === generation &&
          activeScope.current.deviceId === device.core_device_id &&
          activeScope.current.propertyId === propertyId
        );
      },
    ),
  );
  const registryQueryPrefix = [
    'core',
    runtime.apiNamespace,
    generation,
    'access-device-registry',
  ] as const;
  const liveDevice = useSyncExternalStore(
    (onStoreChange) => queryClient.getQueryCache().subscribe(onStoreChange),
    () => {
      const rows = queryClient
        .getQueryCache()
        .findAll({ queryKey: registryQueryPrefix })
        .flatMap((query) => {
          const data = query.state.data as { items?: AccessDeviceRegistryItem[] } | undefined;
          return data?.items ?? [];
        });
      return rows.find((row) => row.core_device_id === device.core_device_id) ?? device;
    },
    () => device,
  );
  const eligibleIds = useMemo(() => {
    const data = candidateContext.data;
    if (!data || data.requires_property_selection || data.command_property_ids.length === 0)
      return [];
    return data.command_property_ids;
  }, [candidateContext.data]);
  const candidateCatalogs = useQueries({
    queries: eligibleIds.map((candidateId) =>
      nativePropertyCatalogQuery(runtime, generation, candidateId, canWrite, () => {
        const current = runtime.session.getSnapshot();
        return (
          current.status === 'authorized' &&
          current.generation === generation &&
          activeScope.current.apiNamespace === runtime.apiNamespace &&
          activeScope.current.generation === generation &&
          activeScope.current.deviceId === device.core_device_id
        );
      }),
    ),
  });
  const selectedCatalog = useQuery(
    nativePropertyCatalogQuery(
      runtime,
      generation,
      propertyId,
      canWrite && Boolean(propertyId),
      () => {
        const current = runtime.session.getSnapshot();
        return (
          current.status === 'authorized' &&
          current.generation === generation &&
          activeScope.current.apiNamespace === runtime.apiNamespace &&
          activeScope.current.generation === generation &&
          activeScope.current.deviceId === device.core_device_id &&
          activeScope.current.propertyId === propertyId
        );
      },
    ),
  );
  const importedBranch = Boolean(candidateContext.data?.requires_property_selection);
  const validProperty = useMemo(() => {
    if (!propertyId || !context.data || context.data.requires_property_selection) return false;
    if (context.data.command_property_ids.length !== 1) return false;
    if (context.data.command_property_ids[0] !== propertyId) return false;
    const property = selectedCatalog.data?.data.property;
    return property?.id === propertyId;
  }, [context.data, propertyId, selectedCatalog.data]);
  const contextMatchesRoot = context.data?.revision === liveDevice.revision;
  const rootChanged = liveDevice.revision !== originalRevision.current;
  const requiresReview = rootChanged || reconcileRequired;
  const currentSnapshotKey = JSON.stringify([
    runtime.apiNamespace,
    generation,
    device.core_device_id,
    propertyId,
    liveDevice.revision,
    context.data?.revision,
  ]);
  const acknowledgedSnapshot =
    latestDevice?.revision === liveDevice.revision && reviewedSnapshotKey === currentSnapshotKey
      ? latestDevice
      : undefined;
  const canSave =
    canWrite &&
    validProperty &&
    contextMatchesRoot &&
    !pending &&
    !uncertain &&
    !attempt &&
    !receipt &&
    (!requiresReview || Boolean(acknowledgedSnapshot));

  useEffect(
    () => () => {
      controller.current?.abort();
    },
    [],
  );

  const setValue = <K extends keyof Values>(key: K, value: Values[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    if (reviewedSnapshotKey !== undefined) setReviewedSnapshotKey(undefined);
  };

  const refreshAndReconcile = async () => {
    setError(undefined);
    setLatestDevice(undefined);
    setReviewedSnapshotKey(undefined);
    try {
      const latest = await refreshRegistry();
      if (latest?.core_device_id !== device.core_device_id) {
        setError('최신 정보를 다시 조회하지 못했습니다. 초안은 그대로 보존했습니다.');
        return;
      }
      const latestContext = await context.refetch();
      if (latestContext.isError) {
        setError('최신 정보를 다시 조회하지 못했습니다. 초안은 그대로 보존했습니다.');
        return;
      }
      if (latestContext.data?.revision !== latest.revision) {
        setError('최신 장비 정보와 수정 범위가 아직 일치하지 않습니다. 다시 확인해 주세요.');
        return;
      }
      setLatestDevice(latest);
      setReconcileRequired(true);
      setReviewedSnapshotKey(undefined);
      setError(undefined);
    } catch {
      setLatestDevice(undefined);
      setReviewedSnapshotKey(undefined);
      setError('최신 정보를 다시 조회하지 못했습니다. 초안은 그대로 보존했습니다.');
    }
  };

  const buildBody = () => {
    if (liveDevice.provider !== 'LORA') return null;
    const battery =
      values.battery === ''
        ? null
        : /^[+-]?\d+$/.test(values.battery)
          ? Number(values.battery)
          : Number.NaN;
    if (
      !validOpaqueId(values.deviceId) ||
      !validOpaqueId(values.gatewayId) ||
      (values.deviceTypeProvided && Array.from(values.deviceType).length > 24) ||
      (values.deviceNameProvided && Array.from(values.deviceName).length > 50) ||
      (values.gatewayNameProvided && Array.from(values.gatewayName).length > 50) ||
      (battery !== null &&
        (!Number.isInteger(battery) || battery < -(2 ** 31) || battery > 2 ** 31 - 1))
    )
      return null;
    return Object.freeze({
      command_id: crypto.randomUUID(),
      expected_revision: acknowledgedSnapshot?.revision ?? liveDevice.revision,
      device_id: values.deviceId,
      gateway_id: values.gatewayId,
      is_active: values.isActive,
      slot_ids: Object.freeze([...(acknowledgedSnapshot?.slot_ids ?? liveDevice.slot_ids)]),
      device_type: values.deviceTypeProvided ? values.deviceType : null,
      retired: false,
      device_name: values.deviceNameProvided ? values.deviceName : null,
      gateway_name: values.gatewayNameProvided ? values.gatewayName : null,
      battery,
    });
  };

  const submit = async (sameAttempt = false) => {
    if (flight.current !== null || !canWrite || !validProperty) return;
    let captured: ScopedAttempt | null = attempt;
    if (!sameAttempt) {
      if (!canSave) return;
      const body = buildBody();
      if (!body) {
        setError(
          'ID는 1~50자, 유형은 24자, 이름은 50자 이내여야 합니다. 배터리는 32비트 정수로 입력해 주세요.',
        );
        return;
      }
      captured = Object.freeze({
        property_id: propertyId,
        core_device_id: device.core_device_id,
        body,
        apiNamespace: runtime.apiNamespace,
        generation,
      });
      setAttempt(captured);
    }
    if (!captured) return;
    const activeFlight = {};
    flight.current = activeFlight;
    setPending(true);
    setError(undefined);
    const abort = new AbortController();
    controller.current = abort;
    const isCurrent = () => {
      const current = runtime.session.getSnapshot();
      return (
        !abort.signal.aborted &&
        current.status === 'authorized' &&
        current.generation === captured.generation &&
        runtime.apiNamespace === captured.apiNamespace &&
        activeScope.current.apiNamespace === captured.apiNamespace &&
        activeScope.current.generation === captured.generation &&
        activeScope.current.deviceId === captured.core_device_id &&
        activeScope.current.propertyId === captured.property_id
      );
    };
    try {
      if (!captured.core_device_id) throw new Error('missing Core device UUID');
      const saved = await putAccessDeviceManagement(
        runtime.client,
        captured.property_id,
        captured.core_device_id,
        captured.body,
        abort.signal,
      );
      if (!isCurrent()) return;
      setAttempt(null);
      setUncertain(false);
      setReceipt('변경 저장됨. 실제 장비 연결이나 작동 상태를 확인한 것은 아닙니다.');
      setSavedRevision(saved.revision);
      const rootPrefix = [
        'core',
        captured.apiNamespace,
        captured.generation,
        'access-device-registry',
      ];
      const inventoryProperties = new Set(
        device.connections
          .map((connection) => connection.property_id)
          .filter((id): id is string => id !== null),
      );
      inventoryProperties.add(captured.property_id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: rootPrefix, refetchType: 'active' }),
        ...[...inventoryProperties].flatMap((id) =>
          ['core-access-device-inventory', 'native-access-inventory'].map((resource) =>
            queryClient.invalidateQueries({
              queryKey: ['core', captured.apiNamespace, captured.generation, resource, id],
              refetchType: 'active',
            }),
          ),
        ),
      ]);
    } catch (failure) {
      if (!isCurrent()) return;
      if (isApiError(failure) && failure.status === 403)
        runtime.session.reportDomainAccessDenied('property', generation);
      if (
        isApiError(failure) &&
        (failure.status === 400 || failure.status === 404 || failure.status === 409)
      )
        setAttempt(null);
      if (isApiError(failure) && failure.status === 409) {
        setReconcileRequired(true);
        setReviewedSnapshotKey(undefined);
      }
      if (
        isApiError(failure) &&
        (failure.kind === 'network' ||
          failure.kind === 'timeout' ||
          failure.kind === 'invalid-response' ||
          (failure.status !== undefined && failure.status >= 500))
      )
        setUncertain(true);
      setError(messageFor(failure));
    } finally {
      if (flight.current === activeFlight) {
        flight.current = null;
        setPending(false);
        if (controller.current === abort) controller.current = null;
      }
    }
  };

  const dirty =
    !receipt &&
    (values.deviceId !== device.device_id ||
      values.gatewayId !== device.gateway_id ||
      values.deviceTypeProvided !== (device.device_type !== null) ||
      (values.deviceTypeProvided && values.deviceType !== device.device_type) ||
      values.deviceNameProvided !== (device.device_name !== null) ||
      (values.deviceNameProvided && values.deviceName !== device.device_name) ||
      values.gatewayNameProvided !== (device.gateway_name !== null) ||
      (values.gatewayNameProvided && values.gatewayName !== device.gateway_name) ||
      values.battery !== (device.battery === null ? '' : String(device.battery)) ||
      values.isActive !== device.is_active ||
      attempt !== null ||
      uncertain);
  const close = () => {
    controller.current?.abort();
    onClose();
  };
  const queryError = context.isError
    ? isApiError(context.error) && context.error.status === 404
      ? '수정할 지점 범위를 확인하지 못했습니다. 지점을 추정하지 않았습니다.'
      : '수정 범위를 확인하지 못했습니다. 다시 조회해 주세요.'
    : undefined;

  return (
    <section aria-label="기존 장비 정보 수정" className="space-y-4 rounded-md border p-4 sm:p-5">
      <UnsavedGuard
        when={canWrite && (dirty || pending)}
        suppressConfirmation={closeConfirmOpen}
        autoProceedWhenClean={false}
        warning={{
          target: uncertain ? '결과가 불확실한 변경 요청' : '저장하지 않은 장비 변경',
          action: '초안을 버리고 이동',
          consequence: uncertain
            ? '앞선 요청이 이미 저장되었거나 처리 중일 수 있습니다. 화면을 떠나면 같은 요청을 다시 확인할 수 없습니다.'
            : '이동하면 입력한 변경 내용을 잃습니다.',
        }}
      />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="title-sm">기존 장비 정보 수정</h2>
          <p className="body-sm text-muted-foreground mt-1">
            저장된 정보를 수정합니다. 연결 공간은 현재 상태 그대로 유지됩니다.
          </p>
        </div>
        <Button
          variant="outline"
          disabled={pending}
          onClick={() => {
            if (dirty || pending || uncertain) {
              setConfirmAction('close');
              setCloseConfirmOpen(true);
            } else close();
          }}
        >
          닫기
        </Button>
      </div>
      <FormField label="수정 범위 지점" htmlFor="edit-device-property">
        <Select
          value={propertyId}
          disabled={pending || uncertain || Boolean(attempt)}
          onValueChange={(value) => {
            if (!value) return;
            setPropertyId(value);
            setError(undefined);
            setReviewedSnapshotKey(undefined);
          }}
        >
          <SelectTrigger
            id="edit-device-property"
            aria-label="수정 범위 지점"
            className="min-h-11 w-full"
          >
            <SelectValue placeholder="지점을 선택하세요">
              {(value) => {
                const label =
                  typeof value === 'string'
                    ? labelFor(properties.data?.data ?? [], value)
                    : undefined;
                return label ?? (typeof value === 'string' ? value : '');
              }}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {importedBranch
              ? (properties.data?.data ?? []).map((property) => (
                  <SelectItem key={property.id} value={property.id}>
                    {property.name} · {property.code}
                  </SelectItem>
                ))
              : eligibleIds.map((id) => {
                  const result = candidateCatalogs[eligibleIds.indexOf(id)]?.data?.data;
                  const item = properties.data?.data.find((row) => row.id === id);
                  const label = item
                    ? `${item.name} · ${item.code}`
                    : result
                      ? `${result.property.name} · ${result.property.code}`
                      : undefined;
                  return label ? (
                    <SelectItem key={id} value={id}>
                      {label}
                    </SelectItem>
                  ) : null;
                })}
          </SelectContent>
        </Select>
        {importedBranch && properties.data?.next_cursor && !propertyId && (
          <Button
            className="mt-2"
            variant="outline"
            disabled={properties.isFetching}
            onClick={() => {
              setCatalogCursor(properties.data.next_cursor);
            }}
          >
            다음 지점 50개
          </Button>
        )}
      </FormField>
      {properties.isPending && canWrite && <p role="status">지점 목록을 불러오고 있습니다.</p>}
      {properties.isError && <p role="alert">지점 목록을 불러오지 못했습니다.</p>}
      {context.isPending && <p role="status">수정 가능한 지점을 확인하고 있습니다.</p>}
      {queryError && (
        <Alert variant="warning">
          <AlertDescription>{queryError}</AlertDescription>
          <Button
            variant="outline"
            disabled={context.isFetching}
            onClick={() => void context.refetch()}
          >
            다시 확인
          </Button>
        </Alert>
      )}
      {!context.isPending && context.data?.requires_property_selection && (
        <p className="body-sm text-muted-foreground">수정할 수 있는 지점을 선택해 주세요.</p>
      )}
      {!context.isPending &&
        context.data &&
        !context.data.requires_property_selection &&
        context.data.command_property_ids.length === 0 && (
          <p role="alert">현재 수정할 수 있는 지점이 없습니다.</p>
        )}
      {propertyId && context.isFetching && (
        <p role="status">선택한 지점의 수정 범위를 확인하고 있습니다.</p>
      )}
      {propertyId && selectedCatalog.isError && (
        <Alert variant="warning">
          <AlertDescription>
            선택한 지점 정보를 확인하지 못했습니다. 다시 조회해 주세요.
          </AlertDescription>
          <Button variant="outline" onClick={() => void selectedCatalog.refetch()}>
            지점 정보 다시 조회
          </Button>
        </Alert>
      )}
      {(rootChanged || reconcileRequired || context.data?.revision !== liveDevice.revision) &&
        (latestDevice?.revision !== liveDevice.revision ||
          context.data?.revision !== liveDevice.revision) && (
          <Alert variant="warning">
            <AlertDescription>
              저장된 정보와 수정 가능한 지점의 기준이 다릅니다. 초안은 유지했습니다. 최신 정보를
              다시 조회한 뒤 직접 대조해 주세요.
            </AlertDescription>
            <Button
              variant="outline"
              disabled={pending || uncertain}
              onClick={() => void refreshAndReconcile()}
            >
              최신 정보 다시 조회
            </Button>
          </Alert>
        )}
      {(rootChanged || reconcileRequired) &&
        latestDevice?.revision === liveDevice.revision &&
        context.data?.revision === liveDevice.revision && (
          <details className="rounded border p-3">
            <summary className="cursor-pointer">다시 조회한 저장 정보와 초안 대조</summary>
            <dl className="mt-2 grid gap-2 sm:grid-cols-2">
              <div>
                <dt className="caption">현재 장비 ID</dt>
                <dd className="break-all">{latestDevice.device_id}</dd>
              </div>
              <div>
                <dt className="caption">현재 게이트웨이 ID</dt>
                <dd className="break-all">{latestDevice.gateway_id}</dd>
              </div>
              <div>
                <dt className="caption">현재 장비 이름</dt>
                <dd>{latestDevice.device_name ?? '미제공'}</dd>
              </div>
              <div>
                <dt className="caption">현재 게이트웨이 이름</dt>
                <dd>{latestDevice.gateway_name ?? '미제공'}</dd>
              </div>
              <div>
                <dt className="caption">현재 장비 유형</dt>
                <dd>{latestDevice.device_type ?? '미제공'}</dd>
              </div>
              <div>
                <dt className="caption">현재 배터리 관측값</dt>
                <dd>{latestDevice.battery === null ? '미제공' : String(latestDevice.battery)}</dd>
              </div>
              <div className="grid grid-cols-2 gap-3 rounded border p-3 sm:col-span-2">
                <div>
                  <dt className="caption">현재 저장된 활성 설정</dt>
                  <dd>{latestDevice.is_active ? '활성으로 저장' : '비활성으로 저장'}</dd>
                </div>
                <div>
                  <dt className="caption">현재 유지할 초안 활성 설정</dt>
                  <dd>{values.isActive ? '활성으로 저장' : '비활성으로 저장'}</dd>
                </div>
              </div>
            </dl>
            {(rootChanged || reconcileRequired) &&
              latestDevice.revision === liveDevice.revision &&
              context.data.revision === liveDevice.revision && (
                <label className="caption mt-3 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={reviewedSnapshotKey === currentSnapshotKey}
                    onChange={(event) => {
                      setReviewedSnapshotKey(event.target.checked ? currentSnapshotKey : undefined);
                    }}
                  />
                  최신 저장 정보와 초안을 대조했고, 이 초안을 현재 기준으로 저장합니다.
                </label>
              )}
          </details>
        )}
      {validProperty && context.data && (
        <>
          <p className="body-sm">
            선택한 지점:{' '}
            {labelFor(properties.data?.data ?? [], propertyId) ??
              selectedCatalog.data?.data.property.name}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label="장비 ID"
              htmlFor="edit-device-id"
              message="원문 그대로 저장 · 1~50 코드 포인트"
            >
              <Input
                id="edit-device-id"
                value={values.deviceId}
                disabled={pending || uncertain || Boolean(attempt) || Boolean(receipt)}
                onChange={(event) => {
                  setValue('deviceId', event.target.value);
                }}
              />
            </FormField>
            <FormField
              label="게이트웨이 ID"
              htmlFor="edit-gateway-id"
              message="원문 그대로 저장 · 1~50 코드 포인트"
            >
              <Input
                id="edit-gateway-id"
                value={values.gatewayId}
                disabled={pending || uncertain || Boolean(attempt) || Boolean(receipt)}
                onChange={(event) => {
                  setValue('gatewayId', event.target.value);
                }}
              />
            </FormField>
            <NullableTextField
              label="장비 유형"
              id="edit-device-type"
              value={values.deviceType}
              provided={values.deviceTypeProvided}
              max={24}
              disabled={pending || uncertain || Boolean(attempt) || Boolean(receipt)}
              onChange={(value, provided) => {
                setValue('deviceType', value);
                setValue('deviceTypeProvided', provided);
              }}
            />
            <NullableTextField
              label="장비 이름"
              id="edit-device-name"
              value={values.deviceName}
              provided={values.deviceNameProvided}
              max={50}
              disabled={pending || uncertain || Boolean(attempt) || Boolean(receipt)}
              onChange={(value, provided) => {
                setValue('deviceName', value);
                setValue('deviceNameProvided', provided);
              }}
            />
            <NullableTextField
              label="게이트웨이 이름"
              id="edit-gateway-name"
              value={values.gatewayName}
              provided={values.gatewayNameProvided}
              max={50}
              disabled={pending || uncertain || Boolean(attempt) || Boolean(receipt)}
              onChange={(value, provided) => {
                setValue('gatewayName', value);
                setValue('gatewayNameProvided', provided);
              }}
            />
            <FormField
              label="배터리 관측값"
              htmlFor="edit-device-battery"
              message="저장된 관측값 · 비우면 미제공"
            >
              <Input
                id="edit-device-battery"
                inputMode="numeric"
                value={values.battery}
                disabled={pending || uncertain || Boolean(attempt) || Boolean(receipt)}
                onChange={(event) => {
                  setValue('battery', event.target.value);
                }}
              />
              <p className="caption text-muted-foreground">현재 잔량을 조회하는 기능은 아닙니다.</p>
            </FormField>
          </div>
          <FormField label="저장된 활성 설정">
            <label className="body-sm flex items-center gap-2">
              <input
                type="checkbox"
                checked={values.isActive}
                disabled={pending || uncertain || Boolean(attempt) || Boolean(receipt)}
                onChange={(event) => {
                  setValue('isActive', event.target.checked);
                }}
              />
              활성으로 저장
            </label>
            <p className="caption text-muted-foreground">
              실제 장비 연결이나 작동 상태를 뜻하지 않습니다.
            </p>
          </FormField>
          {error && (
            <Alert variant="warning">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {uncertain && (
            <Button disabled={pending || !canWrite} onClick={() => void submit(true)}>
              같은 변경 다시 확인/재시도
            </Button>
          )}
          {receipt && (
            <p role="status" aria-label="변경 저장됨">
              {receipt}
            </p>
          )}
          {savedRevision !== undefined && (
            <p className="caption">저장된 revision {savedRevision}</p>
          )}
        </>
      )}
      {error && !validProperty && <p role="alert">{error}</p>}
      {session.status === 'authorized' && !canWrite && (
        <p className="body-sm text-muted-foreground">
          현재 지점 수정 권한이 없어 저장할 수 없습니다.
        </p>
      )}
      {!receipt && !uncertain && !attempt && (
        <Button tone="brand" disabled={!canSave || !dirty} onClick={() => void submit(false)}>
          {pending ? '저장 중' : '변경 저장'}
        </Button>
      )}
      <ConfirmDialog
        open={closeConfirmOpen}
        onOpenChange={setCloseConfirmOpen}
        target={uncertain ? '결과가 불확실한 변경 요청' : '기존 장비 변경 초안'}
        action={confirmAction === 'close' ? '초안 폐기 후 닫기' : '초안 폐기'}
        consequence={
          uncertain
            ? '앞선 요청은 이미 저장되었을 수 있습니다. 화면을 떠나도 저장을 되돌릴 수 없으며, 같은 요청을 다시 확인할 내용은 사라집니다.'
            : '아직 저장하지 않은 입력 내용이 사라집니다.'
        }
        onConfirm={close}
      />
    </section>
  );
}

function NullableTextField({
  label,
  id,
  value,
  provided,
  max,
  disabled,
  onChange,
}: {
  label: string;
  id: string;
  value: string;
  provided: boolean;
  max: number;
  disabled: boolean;
  onChange: (value: string, provided: boolean) => void;
}) {
  return (
    <FormField
      label={label}
      htmlFor={id}
      message={
        provided ? `${String(Array.from(value).length)} / ${String(max)} 코드 포인트` : '미제공'
      }
    >
      <label className="caption mb-1 flex items-center gap-2">
        <input
          type="checkbox"
          checked={provided}
          disabled={disabled}
          onChange={(event) => {
            onChange(value, event.target.checked);
          }}
        />
        값 지정
      </label>
      <Input
        id={id}
        value={value}
        disabled={disabled || !provided}
        onChange={(event) => {
          onChange(event.target.value, provided);
        }}
      />
    </FormField>
  );
}
